import { FCX, getFormattedThousand, ID, TextInput } from "@datassential/platform-ui-lib";
import { KeyboardEvent, useCallback, useEffect, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import cn from "classnames";
import SmallLoader from "@components/SmallLoader/SmallLoader";
import { useFoodSocialCustomAssociation } from "@apiHooks/useFoodSocialCustomAssociation";
import { IFoodSocialCustomAssociation } from "@models/FoodSocialCustomAssociation";
import './SocialCustomAssociation.scss';

const isSameQuery = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const FoodSocialCustomAssociation: FCX<{
    wordId: ID;
    selectedQuery?: string;
    onResultClick?: (result: IFoodSocialCustomAssociation | null) => void;
    placeholder?: string;
}> = ({
    wordId,
    selectedQuery,
    onResultClick,
    placeholder = 'Search for associations',
}) => {
    const [query, setQuery] = useState<string>('');
    const [submittedQuery, setSubmittedQuery] = useState<string>('');

    const onSubmit = useCallback(
        () => setSubmittedQuery(query),
        [query],
    );

    useEffect(() => {
        if (query.trim() === '') {
            setSubmittedQuery('');
        }
    }, [query]);

    useEffect(() => {
        setQuery('');
        setSubmittedQuery('');
    }, [wordId]);

    const {
        isLoading,
        isSuccess,
        data,
    } = useFoodSocialCustomAssociation(wordId, submittedQuery);

    const {
        query: resultQuery,
        matchCount,
        oneMonthGrowthPct,
    } = data;

    const isQueryApplied = submittedQuery.trim() !== '';
    const isResultLoading = isQueryApplied && isLoading;
    const isResultCurrent = isSameQuery(resultQuery, submittedQuery);
    const hasResult = isQueryApplied && !isResultLoading && isSuccess === true && isResultCurrent;
    const hasError = isQueryApplied && !isResultLoading && isSuccess === false;
    const isSelected = hasResult && selectedQuery !== undefined && isSameQuery(selectedQuery, resultQuery);

    // drop the selection once the row it belongs to is no longer on screen (new search, cleared input, word change)
    useEffect(() => {
        if (selectedQuery !== undefined && !isSameQuery(selectedQuery, submittedQuery)) {
            onResultClick?.(null);
        }
    }, [selectedQuery, submittedQuery, onResultClick]);

    const onResultToggle = useCallback(
        () => onResultClick?.(isSelected ? null : data),
        [onResultClick, isSelected, data],
    );

    const onResultKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onResultToggle();
            }
        },
        [onResultToggle],
    );

    return (
        <div className="FoodSocialCustomAssociation">
            <div className="FoodSocialCustomAssociation__cols FoodSectionSocial__blur">
                <div className="FoodSocialCustomAssociation__col">
                    <div className="FoodSocialCustomAssociation__search-box">
                        <TextInput
                            value={query}
                            setValue={isResultLoading ? () => undefined : setQuery}
                            onSubmit={isResultLoading ? undefined : onSubmit}
                            placeholder={placeholder}
                            className="FoodSocialCustomAssociation__search"
                            isDisabled={isResultLoading}
                            testId="inputFoodSocialCustomAssociationSearch"
                        />
                        <AiOutlineSearch
                            className="FoodSocialCustomAssociation__search-icon"
                            onClick={isResultLoading ? undefined : onSubmit}
                            data-testid="buttonFoodSocialCustomAssociationSearchIcon"
                        />
                    </div>
                </div>
                <div className="FoodSocialCustomAssociation__col">
                    {isResultLoading && (
                        <div
                            className="FoodSocialCustomAssociation__loader"
                            data-testid="blockFoodSocialCustomAssociationLoader"
                        >
                            <SmallLoader/>
                        </div>
                    )}
                    {hasError && (
                        <div
                            className="FoodSocialCustomAssociation__message"
                            data-testid="textFoodSocialCustomAssociationError"
                        >
                            Something went wrong. Please try again.
                        </div>
                    )}
                    {hasResult && (
                        <div
                            className={cn(
                                "FoodSocialCustomAssociation__result",
                                onResultClick && "is-clickable",
                                isSelected && "is-selected",
                            )}
                            data-testid="blockFoodSocialCustomAssociationResult"
                            role={onResultClick ? "button" : undefined}
                            tabIndex={onResultClick ? 0 : undefined}
                            aria-pressed={onResultClick ? isSelected : undefined}
                            onClick={onResultClick ? onResultToggle : undefined}
                            onKeyDown={onResultClick ? onResultKeyDown : undefined}
                        >
                            <div
                                className="FoodSocialCustomAssociation__result-name"
                                data-testid="textFoodSocialCustomAssociationResultName"
                            >
                                {submittedQuery}
                            </div>
                            <div
                                className="FoodSocialCustomAssociation__item-value"
                                data-testid="textFoodSocialCustomAssociationResultCount"
                            >
                                {getFormattedThousand(matchCount)}
                            </div>
                            <div
                                className="FoodSocialCustomAssociation__item-value"
                                data-testid="textFoodSocialCustomAssociationResultGrowth"
                            >
                                {(oneMonthGrowthPct ?? 0) > 0 ? '+' : ''}{oneMonthGrowthPct ?? 0}%
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FoodSocialCustomAssociation;
